import { Injectable, signal } from '@angular/core';
import { buildSeedData } from './seed';
import type { DemoStoreData } from './store-types';

const STORAGE_KEY = 'ss_demo_store';
const WRITE_DEBOUNCE_MS = 200;

@Injectable({ providedIn: 'root' })
export class DemoStore {
  private readonly _data = signal<DemoStoreData>(readFromStorage() ?? buildSeedData());
  readonly data = this._data.asReadonly();

  private writeTimer: ReturnType<typeof setTimeout> | null = null;

  snapshot(): DemoStoreData {
    return this._data();
  }

  update(mutator: (data: DemoStoreData) => DemoStoreData): void {
    this._data.set(mutator(this._data()));
    this.scheduleWrite();
  }

  reset(): void {
    const fresh = buildSeedData();
    this._data.set(fresh);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      // storage unavailable — in-memory still holds state
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  seedIfEmpty(): void {
    if (!readFromStorage()) this.reset();
  }

  private scheduleWrite(): void {
    if (this.writeTimer !== null) clearTimeout(this.writeTimer);
    this.writeTimer = setTimeout(() => {
      this.writeTimer = null;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._data()));
      } catch {
        // quota exceeded or storage disabled — in-memory signal still holds
      }
    }, WRITE_DEBOUNCE_MS);
  }
}

function readFromStorage(): DemoStoreData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoStoreData | null;
    if (!parsed || typeof parsed !== 'object' || parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}
